
import React, { useState, useMemo } from 'react';
import { Gamepad2, Trophy, Star, Loader2, Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useGameData } from '@/hooks/use-game-data';
import GameModal from './GameModal';
import DataRecoveryDialog from './DataRecoveryDialog';

const GameWidget = () => {
  const { t } = useTranslation();
  const { gameData, isLoading, lastSyncTime, syncStatus, updateGameData, forceSyncData } = useGameData();
  const [isGameModalOpen, setIsGameModalOpen] = useState(false);
  const [isRecoveryDialogOpen, setIsRecoveryDialogOpen] = useState(false);
  const [isForceSyncing, setIsForceSyncing] = useState(false);

  // Memoize stable values to prevent flickering
  const stableValues = useMemo(() => {
    const hasGameProgress = gameData.bestScore > 0 || gameData.totalGamesPlayed > 0 || gameData.unlockedFacts.length > 0;
    const currentLevel = Math.floor(gameData.totalGamesPlayed / 5) + 1;
    
    return {
      bestScore: gameData.bestScore,
      level: currentLevel,
      hasGameProgress,
      totalGamesPlayed: gameData.totalGamesPlayed,
      dailyChallengeCompleted: gameData.dailyChallengeCompleted
    };
  }, [gameData.bestScore, gameData.totalGamesPlayed, gameData.unlockedFacts.length, gameData.dailyChallengeCompleted]);

  // Stabilize the sync status calculation
  const currentSyncStatus = useMemo(() => {
    if (isLoading || isForceSyncing) return 'loading';
    if (!navigator.onLine) return 'offline';
    
    switch (syncStatus) {
      case 'never': return 'never_synced';
      case 'empty': return 'synced_empty';
      case 'with_data': return 'synced';
      case 'error': return 'error';
      case 'loading': return 'loading';
      default: return 'never_synced';
    }
  }, [isLoading, isForceSyncing, syncStatus]);

  const getSyncIcon = () => {
    switch (currentSyncStatus) {
      case 'loading':
        return <Loader2 size={12} className="animate-spin" />;
      case 'synced':
        return <Wifi size={12} className="text-green-400" />;
      case 'synced_empty':
        return <Wifi size={12} className="text-blue-400" />;
      case 'offline':
        return <WifiOff size={12} className="text-red-400" />;
      case 'error':
        return <AlertCircle size={12} className="text-red-400" />;
      case 'never_synced':
        return <RefreshCw size={12} className="text-orange-400" />;
      default:
        return null;
    }
  };

  const getSyncText = () => {
    switch (currentSyncStatus) {
      case 'loading':
        return t('game.syncing', { defaultValue: 'Synchronisation...' });
      case 'synced':
        return t('game.synced', { defaultValue: 'Synchronisé avec données' });
      case 'synced_empty':
        return t('game.synced_empty', { defaultValue: 'Synchronisé (nouveau joueur)' });
      case 'offline':
        return t('game.offline', { defaultValue: 'Hors ligne' });
      case 'error':
        return t('game.sync_error', { defaultValue: 'Erreur de synchronisation' });
      case 'never_synced':
        return t('game.never_synced', { defaultValue: 'Jamais synchronisé' });
      default:
        return '';
    }
  };

  const handleDataRecovered = async (recoveredData: any) => {
    console.log('🎮 GameWidget - Data recovered, updating game data:', recoveredData);
    await updateGameData(recoveredData);
  };

  const handleForceSync = async () => {
    console.log('🎮 GameWidget - Force sync requested');
    setIsForceSyncing(true);
    try {
      await forceSyncData();
    } catch (error) {
      console.error('🎮 GameWidget - Error forcing sync:', error);
    } finally {
      setIsForceSyncing(false);
    }
  };

  // Display stable values to prevent flickering
  const displayBestScore = isLoading ? '...' : stableValues.bestScore;
  const displayLevel = isLoading ? '...' : stableValues.level;

  return (
    <>
      <Card className="mb-6 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-2 rounded-full">
                <Gamepad2 size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg">{t('game.title', { defaultValue: 'Trésors de Djibouti' })}</h3>
                <p className="text-sm opacity-90">{t('game.subtitle', { defaultValue: 'Jeu de puzzle culturel' })}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 mb-1">
                <Trophy size={16} />
                <span className="text-sm font-medium">
                  {t('game.bestScore', { defaultValue: 'Meilleur' })}: {displayBestScore}
                </span>
              </div>
              <Button
                onClick={() => setIsGameModalOpen(true)}
                className="bg-white text-purple-600 hover:bg-gray-100 transition-colors"
                size="sm"
                disabled={isLoading}
              >
                <Star size={16} className="mr-1" />
                {t('game.playNow', { defaultValue: 'Jouer' })}
              </Button>
            </div>
          </div>
          
          <div className="mt-3 flex items-center justify-between text-xs opacity-75">
            <div className="flex items-center space-x-2">
              {stableValues.totalGamesPlayed > 0 && !isLoading && (
                <span>
                  {t('game.level', { defaultValue: 'Niveau' })} {displayLevel} • 
                  {stableValues.dailyChallengeCompleted ? ' ✅ ' + t('game.dailyChallenge', { defaultValue: 'Défi quotidien' }) + ' ' + t('game.completed', { defaultValue: 'terminé' }) : ' 🎯 ' + t('game.dailyChallenge', { defaultValue: 'Défi quotidien' })}
                </span>
              )}
              {!stableValues.hasGameProgress && !isLoading && (currentSyncStatus === 'synced_empty' || currentSyncStatus === 'synced') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRecoveryDialogOpen(true)}
                  className="text-white border-white/30 hover:bg-white/10"
                >
                  <RefreshCw size={12} className="mr-1" />
                  Récupérer mes données
                </Button>
              )}
              {(currentSyncStatus === 'error' || currentSyncStatus === 'never_synced') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleForceSync}
                  disabled={isForceSyncing}
                  className="text-white border-white/30 hover:bg-white/10"
                >
                  <RefreshCw size={12} className={`mr-1 ${isForceSyncing ? 'animate-spin' : ''}`} />
                  Resynchroniser
                </Button>
              )}
            </div>
            <div className="flex items-center space-x-1">
              {getSyncIcon()}
              <span>{getSyncText()}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <GameModal 
        isOpen={isGameModalOpen} 
        onClose={() => setIsGameModalOpen(false)} 
      />

      <DataRecoveryDialog
        isOpen={isRecoveryDialogOpen}
        onClose={() => setIsRecoveryDialogOpen(false)}
        onDataRecovered={handleDataRecovered}
      />
    </>
  );
};

export default GameWidget;
